import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import GanttChart from '../components/GanttChart';
import DataTable from '../components/DataTable';
import Modal from '../components/Modal';
import FormField from '../components/FormField';

interface Task {
  id: string;
  name: string;
  start: string;
  end: string;
  progress: number;
  dependencies?: string;
  custom_class?: string;
}

interface OrderSummary {
  id: number;
  customer_name: string;
  title: string;
  start_date: string;
  end_date: string;
}

const Schedule: React.FC = () => {
  const { orderId } = useParams<{ orderId: string }>();
  const navigate = useNavigate();
  const [order, setOrder] = useState<OrderSummary | null>(null);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'Day' | 'Week' | 'Month'>('Week');
  const [showAddModal, setShowAddModal] = useState(false);
  const [newTask, setNewTask] = useState({ name: '', start: '', end: '', progress: 0 });

  const loadData = useCallback(async () => {
    if (!orderId) return;
    setLoading(true);
    setError(null);
    try {
      const [orderRes, tasksRes] = await Promise.all([
        window.api.order.get(Number(orderId)),
        window.api.schedule.list({ order_id: Number(orderId) }),
      ]);
      if (!orderRes.ok) throw new Error(orderRes.error || '注文データの取得に失敗');
      if (!tasksRes.ok) throw new Error(tasksRes.error || '工程データの取得に失敗');
      setOrder(orderRes.data);
      setTasks(tasksRes.data || []);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [orderId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleGenerateFromOrder = async () => {
    if (!orderId) return;
    try {
      const res = await window.api.schedule.generateFromOrder(Number(orderId));
      if (!res.ok) throw new Error(res.error || '生成に失敗');
      await loadData();
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleSave = async () => {
    try {
      const res = await window.api.schedule.bulkUpdate(tasks);
      if (!res.ok) throw new Error(res.error || '保存に失敗');
      alert('保存しました');
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleExportPdf = async () => {
    if (!orderId) return;
    try {
      const res = await window.api.pdf.schedule(Number(orderId));
      if (!res.ok) throw new Error(res.error || 'PDF出力に失敗');
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleAddTask = async () => {
    if (!newTask.name || !newTask.start || !newTask.end) {
      alert('タスク名・開始日・終了日は必須です');
      return;
    }
    try {
      const res = await window.api.schedule.create({
        order_id: Number(orderId),
        name: newTask.name,
        start: newTask.start,
        end: newTask.end,
        progress: newTask.progress,
      });
      if (!res.ok) throw new Error(res.error || '追加に失敗');
      setShowAddModal(false);
      setNewTask({ name: '', start: '', end: '', progress: 0 });
      await loadData();
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleDeleteTask = async (taskId: string) => {
    if (!confirm('この工程を削除しますか？')) return;
    try {
      const res = await window.api.schedule.delete(Number(taskId));
      if (!res.ok) throw new Error(res.error || '削除に失敗');
      await loadData();
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleDateChange = (id: string, start: string, end: string) => {
    setTasks((prev) =>
      prev.map((t) => (t.id === id ? { ...t, start, end } : t))
    );
  };

  const handleProgressChange = (id: string, progress: number) => {
    setTasks((prev) =>
      prev.map((t) => (t.id === id ? { ...t, progress } : t))
    );
  };

  if (loading) {
    return (
      <div style={{ padding: '24px', textAlign: 'center', color: '#6b7280' }}>
        読み込み中...
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '24px', color: '#ef4444' }}>
        エラー: {error}
        <button className="btn-default" onClick={loadData} style={{ marginLeft: '12px' }}>
          再試行
        </button>
      </div>
    );
  }

  const taskColumns = [
    { key: 'id', label: 'ID', width: '60px' },
    { key: 'name', label: '工程名' },
    { key: 'start', label: '開始日', width: '120px' },
    { key: 'end', label: '終了日', width: '120px' },
    {
      key: 'progress',
      label: '進捗(%)',
      width: '100px',
      align: 'right' as const,
      format: (v: number) => `${v}%`,
    },
    {
      key: 'actions',
      label: '操作',
      width: '80px',
      align: 'center' as const,
      format: () => '',
    },
  ];

  const taskRows = tasks.map((t) => ({
    ...t,
    actions: (
      <button
        className="btn-danger"
        style={{ padding: '4px 8px', fontSize: '12px' }}
        onClick={(e) => {
          e.stopPropagation();
          handleDeleteTask(t.id);
        }}
      >
        削除
      </button>
    ),
  }));

  return (
    <div style={{ padding: '24px', maxWidth: '1200px', margin: '0 auto' }}>
      {/* ヘッダー */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h1>工程表</h1>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button className="btn-default" onClick={() => navigate(-1)}>
            戻る
          </button>
          <button className="btn-default" onClick={handleGenerateFromOrder}>
            注文請書から生成
          </button>
          <button className="btn-default" onClick={handleExportPdf}>
            PDF出力
          </button>
          <button className="btn-primary" onClick={handleSave}>
            保存
          </button>
        </div>
      </div>

      {/* 注文サマリ */}
      {order && (
        <div
          style={{
            backgroundColor: '#fff',
            borderRadius: '8px',
            padding: '16px 20px',
            marginBottom: '20px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          }}
        >
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px' }}>
            <div>
              <span style={{ color: '#6b7280', fontSize: '12px' }}>顧客名</span>
              <p style={{ fontWeight: 500 }}>{order.customer_name}</p>
            </div>
            <div>
              <span style={{ color: '#6b7280', fontSize: '12px' }}>件名</span>
              <p style={{ fontWeight: 500 }}>{order.title}</p>
            </div>
            <div>
              <span style={{ color: '#6b7280', fontSize: '12px' }}>期間</span>
              <p style={{ fontWeight: 500 }}>
                {order.start_date} 〜 {order.end_date}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* ガントチャート */}
      <div
        style={{
          backgroundColor: '#fff',
          borderRadius: '8px',
          padding: '16px',
          marginBottom: '20px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
          <h3>ガントチャート</h3>
          <select
            value={viewMode}
            onChange={(e) => setViewMode(e.target.value as any)}
            style={{ padding: '4px 8px' }}
          >
            <option value="Day">日</option>
            <option value="Week">週</option>
            <option value="Month">月</option>
          </select>
        </div>
        <GanttChart
          tasks={tasks}
          viewMode={viewMode}
          onDateChange={handleDateChange}
          onProgressChange={handleProgressChange}
        />
      </div>

      {/* 工程テーブル */}
      <div
        style={{
          backgroundColor: '#fff',
          borderRadius: '8px',
          padding: '16px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
          <h3>工程一覧</h3>
          <button className="btn-primary" onClick={() => setShowAddModal(true)}>
            工程追加
          </button>
        </div>
        <DataTable
          columns={taskColumns}
          rows={taskRows}
          emptyMessage="工程が登録されていません。「注文請書から生成」または「工程追加」ボタンで追加してください。"
        />
      </div>

      {/* 追加モーダル */}
      <Modal
        open={showAddModal}
        onClose={() => setShowAddModal(false)}
        title="工程追加"
        footer={
          <>
            <button className="btn-default" onClick={() => setShowAddModal(false)}>
              キャンセル
            </button>
            <button className="btn-primary" onClick={handleAddTask}>
              追加
            </button>
          </>
        }
      >
        <FormField label="工程名" required error={!newTask.name ? '入力してください' : undefined}>
          <input
            type="text"
            value={newTask.name}
            onChange={(e) => setNewTask((p) => ({ ...p, name: e.target.value }))}
            style={{ width: '100%' }}
            placeholder="例: 基礎工事"
          />
        </FormField>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
          <FormField label="開始日" required error={!newTask.start ? '入力してください' : undefined}>
            <input
              type="date"
              value={newTask.start}
              onChange={(e) => setNewTask((p) => ({ ...p, start: e.target.value }))}
              style={{ width: '100%' }}
            />
          </FormField>
          <FormField label="終了日" required error={!newTask.end ? '入力してください' : undefined}>
            <input
              type="date"
              value={newTask.end}
              onChange={(e) => setNewTask((p) => ({ ...p, end: e.target.value }))}
              style={{ width: '100%' }}
            />
          </FormField>
        </div>
        <FormField label="進捗(%)">
          <input
            type="number"
            min={0}
            max={100}
            value={newTask.progress}
            onChange={(e) => setNewTask((p) => ({ ...p, progress: Number(e.target.value) }))}
            style={{ width: '100%' }}
          />
        </FormField>
      </Modal>
    </div>
  );
};

export default Schedule;
